// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import {
    Button,
    Icon,
    Translate,
} from '@plesk/ui-library';
import { TASKS_TABLE } from 'admin/task/constants/test';
import {
    ITaskResponse,
    TASK_STATUS,
} from 'common/api/resources/Task';
import { dataCySelector } from 'common/tests/selectors';
import Spinner from 'common/components/Spinner/Spinner';
import {
    ButtonWrapper,
    CanceledIcon,
    Container,
    DoneWithErrorsIcon,
    FailedIcon,
    Progress,
    Row,
    RunningContainer,
    SuccessIcon,
    TextWrapper,
    Title,
    WaitingIconWrapper,
} from 'admin/task/components/Styles';

export interface ITaskStatusProps {
    item: ITaskResponse;
    onOpen: () => void;
}

export const TASK_STATUS_MAP = {
    [TASK_STATUS.NOT_STARTED]: <Translate content="task.status.notStarted" />,
    [TASK_STATUS.PENDING]: <Translate content="task.status.pending" />,
    [TASK_STATUS.STATUS_WAITING_FOR_PREVIOUS_TASKS]: <Translate content="task.status.waitingForPreviousTasks" />,
    [TASK_STATUS.QUEUED]: <Translate content="task.status.queued" />,
    [TASK_STATUS.RUNNING]: <Translate content="task.status.running" />,
    [TASK_STATUS.CANCELED]: <Translate content="task.status.canceled" />,
    [TASK_STATUS.FAILED]: <Translate content="task.status.failed" />,
    [TASK_STATUS.DONE]: <Translate content="task.status.done" />,
    [TASK_STATUS.DONE_WITH_ERRORS]: <Translate content="task.status.doneWithErrors" />,
};

const TASK_STATUS_INTENT_MAP = {
    [TASK_STATUS.RUNNING]: 'running',
    [TASK_STATUS.PENDING]: 'waiting',
    [TASK_STATUS.STATUS_WAITING_FOR_PREVIOUS_TASKS]: 'waiting',
    [TASK_STATUS.QUEUED]: 'waiting',
    [TASK_STATUS.CANCELED]: 'canceled',
    [TASK_STATUS.FAILED]: 'failed',
    // Not 'failed': the task did what it was asked and only its cleanup fell short. A delete
    // reported this way means the server really is gone, so showing it as an outright failure
    // tells an administrator the opposite of the truth.
    [TASK_STATUS.DONE_WITH_ERRORS]: 'doneWithErrors',
    [TASK_STATUS.DONE]: 'success',
};

const STATUSES_ICONS: Record<string, React.FC> = {
    running: () => <Spinner />,
    waiting: () => (
        <WaitingIconWrapper>
            <Icon size={16} name="three-dots-horizontal" />
        </WaitingIconWrapper>
    ),
    canceled: () => <CanceledIcon />,
    failed: () => <FailedIcon name="exclamation-mark-circle-filled" size={16} />,
    doneWithErrors: () => <DoneWithErrorsIcon name="triangle-exclamation-mark" size={16} />,
    success: () => <SuccessIcon name="check-mark-circle-filled" size={16} />,
};

const STATUS_WITH_BUTTONS = [
    TASK_STATUS.FAILED,
    TASK_STATUS.DONE,
    TASK_STATUS.DONE_WITH_ERRORS,
];

export const TaskStatus: React.FC<ITaskStatusProps> = ({
    item,
    onOpen,
}) => {
    const StatusIcon = STATUSES_ICONS[TASK_STATUS_INTENT_MAP[item.status]];

    const buttonEl = (
        <Button
            data-cy={dataCySelector(item.id, TASKS_TABLE.MODAL_LINK)}
            onClick={onOpen}
            ghost={true}
            icon="box-diagonal-top-in"
        />
    );

    const showButton = STATUS_WITH_BUTTONS.includes(item.status as TASK_STATUS) && item?.output?.length > 0;

    if (item.status === TASK_STATUS.RUNNING) {
        return (
            <RunningContainer>
                <Row>
                    <div>
                        {StatusIcon && <StatusIcon />}
                    </div>

                    <TextWrapper>
                        <Title>{TASK_STATUS_MAP[item.status]}</Title>
                        <Progress>{item.progress}%</Progress>
                    </TextWrapper>
                </Row>

                <ButtonWrapper>
                    {buttonEl}
                </ButtonWrapper>
            </RunningContainer>
        );
    }

    return (
        <Container>
            <Row>
                <div>
                    {StatusIcon && <StatusIcon />}
                </div>

                <TextWrapper>
                    <Title>
                        {TASK_STATUS_MAP[item.status]}
                    </Title>
                </TextWrapper>
            </Row>

            {showButton && buttonEl}
        </Container>
    );
};
